import { useCallback, useEffect, useState } from 'react'
import axios from 'lib/axios'
import { isStaleAccountSessionError } from 'lib/accountSessionState'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { nexaAIPollIntervalSelector, setNexaAIStatus, setNexaAIAgentState } from 'store/user/userSlice'
import { AGENT_READY_STATES, AGENT_ACTIVATING_STATES, AGENT_MAX_POLL_DURATION_MS, AgentStateType } from 'models'

let inflightPromise: Promise<void> | null = null

export function useNexaAIStatus({ poll = true } = {}) {
  const dispatch = useAppDispatch()
  const pollIntervalMs = useAppSelector(nexaAIPollIntervalSelector)
  const [loading, setLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(true)

  const fetchNexaAIStatus = useCallback(async () => {
    if (inflightPromise) return inflightPromise
    inflightPromise = (async () => {
      try {
        const initResponse = await axios.get('/api/v4/agents/initialize')
        const initialized = initResponse.data?.initialized ?? false
        setIsInitialized(initialized)
        if (initialized) {
          const agentsResponse = await axios.get('/api/v4/agents')
          const agents = agentsResponse.data?.agents || []
          if (agents.length > 0) {
            const agent = agents[0]
            if (agent.state_type === AgentStateType.DATA_REFRESHING) {
              dispatch(setNexaAIStatus({ enabled: true, agentName: agent.name }))
              dispatch(setNexaAIAgentState(AgentStateType.DATA_REFRESHING))
            } else if (AGENT_READY_STATES.includes(agent.state_type)) {
              dispatch(setNexaAIStatus({ enabled: true, agentName: agent.name }))
            } else if (AGENT_ACTIVATING_STATES.includes(agent.state_type)) {
              dispatch(setNexaAIAgentState(agent.state_type))
            } else if (agent.state_type === AgentStateType.REMOVING) {
              dispatch(setNexaAIAgentState(AgentStateType.REMOVING))
            } else {
              dispatch(setNexaAIStatus({ enabled: false, agentName: null }))
            }
          } else {
            dispatch(setNexaAIStatus({ enabled: false, agentName: null }))
          }
        } else {
          // Not initialized — agent is not set up
          dispatch(setNexaAIStatus({ enabled: false, agentName: null }))
        }
      } catch (err) {
        // Account switched while loading; ignore the leftover reply so it can't overwrite the new account's status.
        if (isStaleAccountSessionError(err)) return
        console.error('Error fetching Nexa AI Agent status:', err)
        setIsInitialized(false)
        dispatch(setNexaAIStatus({ enabled: false, agentName: null }))
      } finally {
        setLoading(false)
        inflightPromise = null
      }
    })()
    return inflightPromise
  }, [dispatch])

  // Fetch on mount
  useEffect(() => {
    fetchNexaAIStatus()
  }, [fetchNexaAIStatus])

  // Limit scheduled polling, then recheck on focus while the operation is still in progress.
  useEffect(() => {
    if (!pollIntervalMs || !poll) return

    const deadline = Date.now() + AGENT_MAX_POLL_DURATION_MS
    const pollTimerId = setInterval(() => {
      if (Date.now() >= deadline) {
        clearInterval(pollTimerId)
      }
      fetchNexaAIStatus()
    }, pollIntervalMs)

    const handleFocus = () => {
      if (Date.now() >= deadline) fetchNexaAIStatus()
    }
    window.addEventListener('focus', handleFocus)

    return () => {
      clearInterval(pollTimerId)
      window.removeEventListener('focus', handleFocus)
    }
  }, [pollIntervalMs, poll, fetchNexaAIStatus])

  return { loading, isInitialized }
}
